import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Folder, Trash2, Save, Pencil } from 'lucide-react';
import { useCreativeStore, useEditorStore, useTemplateStore, useAnimationStore } from '@/stores';
import { getFormatById } from '@/components/providers';
import type { Campaign, Creative } from '@/types/creative';
import { captureThumbnail } from '@/components/export/StaticExporter';
import { NewCreativeDialog } from '@/components/ui/NewCreativeDialog';

export function Sidebar() {
  const {
    campaigns,
    creatives,
    currentCampaignId,
    currentCreativeId,
    selectCampaign,
    selectCreative,
    createCampaign,
    createCreative,
    updateCreative,
    updateCampaign,
    deleteCreative,
    deleteCampaign,
  } = useCreativeStore();

  const { selectedFormat, content } = useEditorStore();
  const { currentTemplate, variableValues } = useTemplateStore();
  const { timeline } = useAnimationStore();

  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false);
  const [newCampaignName, setNewCampaignName] = useState('');
  const [showNewCreativeDialog, setShowNewCreativeDialog] = useState(false);

  const currentCampaignCreatives = creatives.filter(
    (c) => c.campaignId === currentCampaignId
  );

  const handleCreateCampaign = async () => {
    if (newCampaignName.trim()) {
      const campaign = await createCampaign(newCampaignName.trim());
      selectCampaign(campaign.id);
      setNewCampaignName('');
      setIsCreatingCampaign(false);
    }
  };

  const handleCreateCreative = async () => {
    if (!currentCampaignId || !currentTemplate) return;

    // Capture thumbnail
    let thumbnailUrl: string | undefined;
    const canvasElement = document.getElementById('ad-canvas');
    if (canvasElement) {
      try {
        thumbnailUrl = await captureThumbnail(canvasElement);
      } catch (e) {
        console.warn('Failed to capture thumbnail:', e);
      }
    }

    // Save current state to creative
    const creative = await createCreative(
      currentCampaignId,
      `${selectedFormat.name} - ${currentTemplate.metadata.name}`,
      selectedFormat.id,
      currentTemplate.metadata.id,
      content
    );

    // Update with full template data
    await updateCreative(creative.id, {
      templateData: JSON.stringify(currentTemplate),
      timelineData: timeline ? JSON.stringify(timeline) : undefined,
      variableValues: variableValues,
      thumbnailUrl,
    });

    selectCreative(creative.id);
  };

  const handleSaveCreative = async () => {
    if (!currentCreativeId || !currentTemplate) return;

    // Capture thumbnail
    let thumbnailUrl: string | undefined;
    const canvasElement = document.getElementById('ad-canvas');
    if (canvasElement) {
      try {
        thumbnailUrl = await captureThumbnail(canvasElement);
      } catch (e) {
        console.warn('Failed to capture thumbnail:', e);
      }
    }

    await updateCreative(currentCreativeId, {
      content,
      templateData: JSON.stringify(currentTemplate),
      timelineData: timeline ? JSON.stringify(timeline) : undefined,
      variableValues: variableValues,
      thumbnailUrl,
    });
  };

  const handleSelectCreative = (creative: Creative) => {
    // Just select the creative - useProjectLoader hook handles loading data
    selectCreative(creative.id);
  };

  const handleDeleteCampaign = async (e: React.MouseEvent, campaignId: string) => {
    e.stopPropagation();
    if (window.confirm('Delete this campaign and all its creatives?')) {
      await deleteCampaign(campaignId);
    }
  };

  return (
    <>
    <aside className="w-56 border-r border-white/5 bg-navy-dark/30 flex flex-col overflow-hidden">
      {/* Campaigns Section */}
      <div className="p-4 border-b border-white/5">
        <div className="flex items-center justify-between mb-1">
          <h2 className="label mb-0">Campaigns</h2>
          <button
            onClick={() => setIsCreatingCampaign(true)}
            className="text-fg-muted hover:text-fg text-xs"
          >
            + New
          </button>
        </div>
        <p className="text-[10px] text-fg-subtle mb-3">Organize your ad initiatives</p>

        {isCreatingCampaign && (
          <div className="mb-3">
            <input
              type="text"
              value={newCampaignName}
              onChange={(e) => setNewCampaignName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateCampaign()}
              placeholder="Campaign name"
              className="input text-xs mb-2"
              autoFocus
            />
            <div className="flex gap-2">
              <button onClick={handleCreateCampaign} className="btn-primary text-xs py-1 px-2">
                Create
              </button>
              <button
                onClick={() => {
                  setIsCreatingCampaign(false);
                  setNewCampaignName('');
                }}
                className="text-xs text-fg-muted hover:text-fg"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="space-y-1">
          {campaigns.length === 0 && !isCreatingCampaign && (
            <p className="text-xs text-fg-subtle italic">No campaigns yet</p>
          )}
          {campaigns.map((campaign) => (
            <CampaignItem
              key={campaign.id}
              campaign={campaign}
              isSelected={campaign.id === currentCampaignId}
              onClick={() => selectCampaign(campaign.id)}
              onDelete={(e) => handleDeleteCampaign(e, campaign.id)}
              onRename={(name) => updateCampaign(campaign.id, { name })}
            />
          ))}
        </div>
      </div>

      {/* Creatives Section */}
      <div className="flex-1 p-4 overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h2 className="label mb-0">Creatives</h2>
          <div className="flex items-center gap-2">
            {currentCreativeId && (
              <button
                onClick={handleSaveCreative}
                className="text-fg-muted hover:text-primary text-xs flex items-center gap-1"
                title="Save current state"
              >
                <Save size={12} />
              </button>
            )}
            {currentCampaignId && (
              <button
                onClick={() => setShowNewCreativeDialog(true)}
                className="text-fg-muted hover:text-fg text-xs"
              >
                + Add
              </button>
            )}
          </div>
        </div>
        <p className="text-[10px] text-fg-subtle mb-3">Designs that adapt to any format</p>

        {!currentCampaignId ? (
          <p className="text-xs text-fg-subtle italic">Select a campaign</p>
        ) : currentCampaignCreatives.length === 0 ? (
          <p className="text-xs text-fg-subtle italic">No creatives yet</p>
        ) : (
          <div className="space-y-2">
            {currentCampaignCreatives.map((creative) => (
              <CreativeItem
                key={creative.id}
                creative={creative}
                isSelected={creative.id === currentCreativeId}
                onClick={() => handleSelectCreative(creative)}
                onDelete={() => deleteCreative(creative.id)}
                onRename={(name) => updateCreative(creative.id, { name })}
              />
            ))}
          </div>
        )}
      </div>
    </aside>

    <NewCreativeDialog
      isOpen={showNewCreativeDialog}
      onClose={() => setShowNewCreativeDialog(false)}
    />
    </>
  );
}

function CampaignItem({
  campaign,
  isSelected,
  onClick,
  onDelete,
  onRename,
}: {
  campaign: Campaign;
  isSelected: boolean;
  onClick: () => void;
  onDelete: (e: React.MouseEvent) => void;
  onRename: (name: string) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(campaign.name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSubmit = useCallback(() => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== campaign.name) {
      onRename(trimmed);
    }
    setIsEditing(false);
  }, [editName, campaign.name, onRename]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    } else if (e.key === 'Escape') {
      setEditName(campaign.name);
      setIsEditing(false);
    }
  }, [handleSubmit, campaign.name]);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setEditName(campaign.name);
    setIsEditing(true);
  }, [campaign.name]);

  return (
    <div
      onClick={onClick}
      className={`group w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
        isSelected
          ? 'bg-primary/20 text-fg'
          : 'text-fg-muted hover:bg-white/5 hover:text-fg'
      }`}
    >
      <div className="flex items-center min-w-0 flex-1">
        <Folder size={14} className="mr-2 flex-none" />
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleSubmit}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
            className="flex-1 min-w-0 bg-white/10 border border-primary/50 rounded px-1 py-0.5 text-xs text-fg outline-none"
          />
        ) : (
          <span className="truncate" onDoubleClick={handleDoubleClick}>{campaign.name}</span>
        )}
      </div>
      <div className="flex items-center gap-1 flex-none">
        {!isEditing && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setEditName(campaign.name);
              setIsEditing(true);
            }}
            className="opacity-0 group-hover:opacity-100 p-1 text-fg-subtle hover:text-fg transition-opacity"
            title="Rename campaign"
          >
            <Pencil size={12} />
          </button>
        )}
        <button
          onClick={onDelete}
          className="opacity-0 group-hover:opacity-100 p-1 text-fg-subtle hover:text-accent-red transition-opacity"
          title="Delete campaign"
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}

function CreativeItem({
  creative,
  isSelected,
  onClick,
  onDelete,
  onRename,
}: {
  creative: Creative;
  isSelected: boolean;
  onClick: () => void;
  onDelete: () => void;
  onRename: (name: string) => void;
}) {
  const format = getFormatById(creative.primaryFormatId);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(creative.name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSubmit = useCallback(() => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== creative.name) {
      onRename(trimmed);
    }
    setIsEditing(false);
  }, [editName, creative.name, onRename]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    } else if (e.key === 'Escape') {
      setEditName(creative.name);
      setIsEditing(false);
    }
  }, [handleSubmit, creative.name]);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setEditName(creative.name);
    setIsEditing(true);
  }, [creative.name]);

  return (
    <div
      onClick={onClick}
      className={`group cursor-pointer p-2 rounded-lg border transition-colors ${
        isSelected
          ? 'border-primary bg-primary/10'
          : 'border-white/5 hover:border-white/10 bg-white/[0.02]'
      }`}
    >
      {/* Thumbnail */}
      <div className="aspect-video bg-navy rounded mb-2 flex items-center justify-center overflow-hidden">
        {creative.thumbnailUrl ? (
          <img
            src={creative.thumbnailUrl}
            alt={creative.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="opacity-50 text-xs text-fg-muted">
            {creative.templateId || 'Design'}
          </span>
        )}
      </div>
      <div className="flex items-start justify-between gap-1">
        <div className="min-w-0 flex-1">
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onBlur={handleSubmit}
              onKeyDown={handleKeyDown}
              onClick={(e) => e.stopPropagation()}
              className="w-full bg-white/10 border border-primary/50 rounded px-1 py-0.5 text-xs text-fg outline-none"
            />
          ) : (
            <p
              className="text-xs font-medium text-fg truncate cursor-text"
              onDoubleClick={handleDoubleClick}
              title="Double-click to rename"
            >
              {creative.name}
            </p>
          )}
          <p className="text-[10px] text-fg-subtle">
            {format?.width}×{format?.height}
          </p>
        </div>
        <div className="flex items-center gap-0.5 flex-none">
          {!isEditing && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setEditName(creative.name);
                setIsEditing(true);
              }}
              className="opacity-0 group-hover:opacity-100 text-fg-subtle hover:text-fg text-xs p-1 transition-opacity"
              title="Rename creative"
            >
              <Pencil size={10} />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="opacity-0 group-hover:opacity-100 text-fg-subtle hover:text-accent-red text-xs p-1 transition-opacity"
            title="Delete creative"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}
